import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatMenu } from '@angular/material/menu';
import { MunilyDropdownMenuOption } from './dropdown-menu.types';

export type MunilyDropdownMenuLeadingIconMode = 'checkbox' | 'none' | 'checkcircle';

@Component({
  selector: 'common-dropdown-menu',
  templateUrl: './dropdown-menu.component.html',
  styleUrls: ['./dropdown-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'munilyDropdownMenu',
})
export class DropdownMenuComponent {
  @Input() options: MunilyDropdownMenuOption[] = [];
  @Input() selectedId?: string;
  /**
   * Matches UI Kit anatomy:
   * - `checkbox`: leading checkbox icon (selected/unselected)
   * - `none`: no leading icon space
   * - `checkcircle`: show check_circle only for selected option
   */
  @Input() leadingIconMode: MunilyDropdownMenuLeadingIconMode = 'checkbox';
  /** When true, uses `leadingIcon` per option (if present) instead of built-in icons. */
  @Input() useOptionLeadingIcon: boolean = false;

  @Output() optionSelect = new EventEmitter<MunilyDropdownMenuOption>();

  @ViewChild('menu', { static: true }) menu!: MatMenu;

  /**
   * Keyed by `id` so a caller that rebuilds its `options` array (a getter or a method call in a
   * template binding) reuses the existing item buttons instead of destroying them. Without this, a
   * DOM rebuild between `mousedown` and `mouseup` swallows the click entirely and the menu looks
   * broken for no visible reason.
   */
  trackByOptionId(index: number, option: MunilyDropdownMenuOption): string | number {
    return option.id ?? index;
  }

  isSelected(option: MunilyDropdownMenuOption): boolean {
    return !!this.selectedId && option.id === this.selectedId;
  }

  leadingIconLigature(option: MunilyDropdownMenuOption): string | null {
    if (this.leadingIconMode === 'none') return null;

    if (this.useOptionLeadingIcon) {
      return option.leadingIcon ?? null;
    }

    if (this.leadingIconMode === 'checkcircle') {
      return this.isSelected(option) ? 'check_circle' : null;
    }

    // checkbox
    return this.isSelected(option) ? 'check_box' : 'check_box_outline_blank';
  }

  onSelect(option: MunilyDropdownMenuOption): void {
    if (option.disabled) return;
    this.optionSelect.emit(option);
    // MatMenuTrigger will close automatically after click.
  }
}

